/**
 * IntegrationMapView — Main container: state, filters, layout.
 *
 * Composes ForceGraph, FilterPanel, and IntegrationLegend.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from '../../contexts/AdminContextProvider';
import ForceGraph from './ForceGraph';
import FilterPanel from './FilterPanel';
import IntegrationLegend from './IntegrationLegend';
import { RELATIONSHIP_TYPES, type RelationshipType } from './constants';
import { updateNodePosition } from '../../services/integration-map-api';
import type { IntegrationMap, IntegrationNode } from '../../services/integration-map-api';

interface Props {
  data: IntegrationMap;
  onRefresh?: () => void;
}

const ALL_TYPES = new Set(Object.keys(RELATIONSHIP_TYPES) as RelationshipType[]);

const IntegrationMapView: React.FC<Props> = ({ data }) => {
  const { authFetch } = useAuth();
  const { activeTenant } = useAdminContext();
  const [activeTypes, setActiveTypes] = useState<Set<RelationshipType>>(new Set(ALL_TYPES));
  const [weightThreshold, setWeightThreshold] = useState(1);
  const [criticalOnly, setCriticalOnly] = useState(false);

  const toggleType = useCallback((type: RelationshipType) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Filter edges
  const filteredEdges = useMemo(() => {
    return data.edges.filter((e) => {
      if (!activeTypes.has(e.relationshipType as RelationshipType)) return false;
      if (e.weight < weightThreshold) return false;
      if (criticalOnly && !e.isCritical) return false;
      return true;
    });
  }, [data.edges, activeTypes, weightThreshold, criticalOnly]);

  const handleNodePositionSave = useCallback(
    (nodeId: string, x: number, y: number) => {
      updateNodePosition(authFetch, nodeId, x, y, activeTenant?.id).catch(() => {
        // Silently fail — position save is best-effort
      });
    },
    [authFetch, activeTenant],
  );

  const handleNodeClick = useCallback((node: IntegrationNode) => {
    // WQ-246: Dispatches admin-navigate event; Portal's listener calls
    // navigate(`/${areaKey}`) for the actual route change.
    window.dispatchEvent(
      new CustomEvent('admin-navigate', { detail: { tab: node.areaKey } }),
    );
  }, []);

  return (
    <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 220px)', minHeight: '400px' }}>
      {/* Main graph area */}
      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <ForceGraph
          nodes={data.nodes}
          edges={filteredEdges}
          onNodePositionSave={handleNodePositionSave}
          onNodeClick={handleNodeClick}
        />
      </div>

      {/* Sidebar: filters + legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
        <FilterPanel
          activeTypes={activeTypes}
          onToggleType={toggleType}
          weightThreshold={weightThreshold}
          onWeightChange={setWeightThreshold}
          criticalOnly={criticalOnly}
          onCriticalOnlyChange={setCriticalOnly}
        />
        <IntegrationLegend
          nodeCount={data.nodes.length}
          edgeCount={filteredEdges.length}
        />
      </div>
    </div>
  );
};

export default IntegrationMapView;
